import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight } from "lucide-react";
import { CONTACTS } from "../data/services";

const Line = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const STATS = ["H24 / 7 GIORNI SU 7", "VEICOLI DI PROPRIETÀ", "OPERATORI SPECIALIZZATI", "PIATTAFORME AEREE"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="https://images.unsplash.com/photo-1649320316177-775fe2d67ca3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Lavori in quota su ponteggio"
          className="w-full h-full object-cover scale-110 opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/60 to-[#0A0A0B]/30" />
        <div className="absolute inset-0 hero-grid-overlay" />
      </motion.div>

      <motion.div style={{ y: fgY }} className="relative z-10 max-w-7xl mx-auto w-full px-4 lg:px-10 pt-40 md:pt-48 pb-10">
        <Line delay={0.35}>
          <p className="font-mono-tac text-xs sm:text-sm tracking-[0.3em] text-[#FFD500] uppercase font-bold mb-6" data-testid="hero-eyebrow">
            [ Roma — Monterotondo · Est. multiservizi ]
          </p>
        </Line>

        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight" data-testid="hero-headline">
          <Line delay={0.45} className="text-5xl sm:text-7xl lg:text-8xl text-white">
            Il servizio che
          </Line>
          <Line delay={0.58} className="text-5xl sm:text-7xl lg:text-8xl text-[#FFD500]">
            cercavi<span className="text-white">...</span>
          </Line>
          <Line delay={0.71} className="text-2xl sm:text-4xl lg:text-5xl mt-4 text-stroke-yellow">
            Edilizia & Multiservizi in quota
          </Line>
        </h1>

        <Line delay={0.85}>
          <p className="mt-6 max-w-xl text-neutral-300 text-base sm:text-lg leading-relaxed" data-testid="hero-subtitle">
            Ponteggi, facciate, tetti, balconi, potature e pronto intervento H24.
            Un unico referente, mezzi propri, risultati garantiti.
          </p>
        </Line>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contatti"
            data-testid="hero-cta-preventivo"
            className="group inline-flex items-center gap-2 bg-[#FFD500] text-black font-display font-bold uppercase tracking-wide text-lg px-8 py-4 hover:bg-white transition-colors duration-300"
          >
            Richiedi Preventivo Gratuito
            <ArrowDownRight size={20} className="transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <a
            href={CONTACTS.diretto.href}
            data-testid="hero-cta-call"
            className="corner-brackets inline-flex items-center gap-2 border border-white/25 text-white font-display font-bold uppercase tracking-wide text-lg px-8 py-4 hover:text-[#FFD500] transition-colors duration-300"
          >
            <Phone size={18} />
            Chiama Ora
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div key={s} className="py-5 pr-4 border-r border-white/10 last:border-r-0 md:[&:nth-child(2)]:border-r">
              <span className="font-mono-tac text-[10px] text-[#FFD500] tracking-widest">0{i + 1}</span>
              <p className="font-display font-bold uppercase text-sm sm:text-base text-white mt-1 tracking-wide">{s}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
